import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowRight, User, Info } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const stageColors = {
  'Detectada': 'bg-blue-100 text-blue-800',
  'Propuesta': 'bg-yellow-100 text-yellow-800',
  'Presentada': 'bg-purple-100 text-purple-800',
  'Ganada': 'bg-green-100 text-green-800',
  'Perdida': 'bg-red-100 text-red-800',
  'Descartada': 'bg-gray-100 text-gray-800',
};

// Feb 2026 (PR-B B2): closed taxonomy for discard reasons.
const DISCARD_REASON_OPTIONS = [
  { key: 'sin_capacidad_tecnica',  label: 'Sin capacidad técnica o sin perfil disponible' },
  { key: 'precio_no_competitivo',  label: 'Precio no competitivo (no alcanzamos la baja)' },
  { key: 'sin_recursos_o_plazo',   label: 'Sin capacidad de recursos o plazo incompatible' },
  { key: 'alcance_no_estrategico', label: 'Alcance no estratégico para TDS' },
  { key: 'riesgo_contractual',     label: 'Riesgo contractual, solvencia o garantías' },
  { key: 'otra_unidad_typsa',      label: 'Se presenta otra unidad de TYPSA' },
  { key: 'conflicto_interes',      label: 'Conflicto de interés o con cliente existente' },
  { key: 'lote_no_interesante',    label: 'Lote o alcance parcial no interesante' },
  { key: 'deteccion_tardia',       label: 'Detección tardía, sin tiempo' },
  { key: 'otro',                   label: 'Otro' },
];

const RELEVANCE_OPTIONS = ['Alta', 'Media', 'Baja'];

const StateChangeDialog = ({
  open,
  onOpenChange,
  fromStage,
  toStage,
  currentOwner,
  clientType,           // 'publica' | 'privada' | 'typsa'
  currentData = {},     // Snapshot of formData to detect missing Detectada fields
  onConfirm,
  onCancel,
  editorUsers = []
}) => {
  // Ownership: user must explicitly keep or change
  const [ownerMode, setOwnerMode] = useState('keep'); // 'keep' | 'change'
  const [owner, setOwner] = useState('');
  // Comment justifying the transition (obligatorio en Propuesta)
  const [reason, setReason] = useState('');
  // Descartada
  const [primary, setPrimary] = useState('');
  const [secondary, setSecondary] = useState('none');
  const [resolutionDate, setResolutionDate] = useState('');
  const [createFollowup, setCreateFollowup] = useState(true);
  const [followUpActive, setFollowUpActive] = useState(false);
  // Iter 92: responsable del seguimiento de descartada Pública
  const [followUpOwner, setFollowUpOwner] = useState('');
  // Propuesta transition — new fields (Feb 2026)
  const [relevance, setRelevance] = useState('');
  const [relevanceComment, setRelevanceComment] = useState('');
  const [linkCarpeta, setLinkCarpeta] = useState('');
  const [linkSharepoint, setLinkSharepoint] = useState('');
  // Tipos de servicios — obligatorio a partir de Propuesta si está vacío.
  const [serviceTypes, setServiceTypes] = useState([]);
  const [otherServiceType, setOtherServiceType] = useState('');
  // Detectada fields that may still be empty at this point
  const [estimatedValue, setEstimatedValue] = useState('');
  const [expectedDelivery, setExpectedDelivery] = useState('');
  const [deadlineTime, setDeadlineTime] = useState('');
  const [linkPliego, setLinkPliego] = useState('');
  // Presentada transition — new fields (iter 90)
  const [bidModality, setBidModality] = useState(''); // 'solo' | 'ute' | 'subcontratista'
  const [offeredAmount, setOfferedAmount] = useState('');
  const [tdsAmount, setTdsAmount] = useState('');
  const [deliveryDate, setDeliveryDate] = useState('');
  const [bidder, setBidder] = useState('');
  const [discountPct, setDiscountPct] = useState('');
  // Ganada/Perdida transition — iter 91
  const [awardAmount, setAwardAmount] = useState('');
  const [awardeeDiscount, setAwardeeDiscount] = useState('');
  const [numberOfBidders, setNumberOfBidders] = useState('');
  const [finalAwardee, setFinalAwardee] = useState('');
  const [globalPosition, setGlobalPosition] = useState('');
  const [technicalPosition, setTechnicalPosition] = useState('');
  const [economicPosition, setEconomicPosition] = useState('');
  const [resultComments, setResultComments] = useState('');
  const [tdsProjectReference, setTdsProjectReference] = useState('');

  const isDescartada = toStage === 'Descartada';
  const isPresentada = toStage === 'Presentada' && fromStage !== 'Presentada';
  const isPropuesta  = toStage === 'Propuesta' && fromStage !== 'Propuesta';
  const isGanada     = toStage === 'Ganada' && fromStage !== 'Ganada';
  const isPerdida    = toStage === 'Perdida' && fromStage !== 'Perdida';
  const isPublica    = clientType === 'publica';
  const isTypsa      = clientType === 'typsa';
  const isPrivada    = clientType === 'privada';

  // Iter 93: detectar transiciones hacia atrás en el pipeline.
  // Orden lineal: Detectada(0) → Propuesta(1) → Presentada(2) → Ganada/Perdida(3).
  // Descartada es lateral. Rollback desde Descartada a cualquier estado activo también es "atrás".
  const STAGE_ORDER = { Detectada: 0, Propuesta: 1, Presentada: 2, Ganada: 3, Perdida: 3 };
  const isBackward = (
    (fromStage in STAGE_ORDER && toStage in STAGE_ORDER && STAGE_ORDER[toStage] < STAGE_ORDER[fromStage]) ||
    (fromStage === 'Descartada' && toStage !== 'Descartada')
  );

  // On Propuesta transition, force user to complete Detectada fields that were not filled.
  const missingEstimated       = isPropuesta && (currentData.estimated_value === '' || currentData.estimated_value == null);
  const missingExpectedDelivery = isPropuesta && !currentData.expected_delivery_date;
  const missingDeadlineTime    = isPropuesta && isPublica && !currentData.deadline_time;
  const missingLinkPliego      = isPropuesta && isPublica && !currentData.link_pliego;

  useEffect(() => {
    if (!open) return;
    const d = new Date();
    d.setDate(d.getDate() + (isPublica ? 90 : 30));
    setResolutionDate(d.toISOString().slice(0, 10));
    setCreateFollowup(isPublica);
    setFollowUpActive(!!currentData.follow_up_active);
    setFollowUpOwner(currentData.follow_up_owner || '');
    setPrimary('');
    setSecondary('none');
    setOwnerMode('keep');
    setOwner('');
    setReason('');
    setRelevance(currentData.relevance || '');
    setRelevanceComment(currentData.relevance_comment || '');
    setLinkCarpeta(currentData.link_carpeta_trabajo || '');
    setLinkSharepoint(currentData.link_sharepoint || '');
    setServiceTypes(currentData.service_types || []);
    setOtherServiceType(currentData.other_service_type || '');
    setEstimatedValue(currentData.estimated_value ?? '');
    setExpectedDelivery(currentData.expected_delivery_date || '');
    setDeadlineTime(currentData.deadline_time || '');
    setLinkPliego(currentData.link_pliego || '');
    // Presentada
    setBidModality(currentData.bid_modality || '');
    setOfferedAmount(currentData.offered_amount ?? '');
    setTdsAmount(currentData.tds_amount ?? '');
    setDeliveryDate(currentData.delivery_date || '');
    setBidder(currentData.bidder || '');
    setDiscountPct(currentData.discount_percentage ?? '');
    // Ganada / Perdida — iter 91
    setAwardAmount(currentData.award_amount ?? '');
    setAwardeeDiscount(currentData.awardee_discount ?? '');
    setNumberOfBidders(currentData.number_of_bidders ?? '');
    setFinalAwardee(currentData.final_awardee || '');
    setGlobalPosition(currentData.global_position ?? '');
    setTechnicalPosition(currentData.technical_position ?? '');
    setEconomicPosition(currentData.economic_position ?? '');
    setResultComments(currentData.result_comments || '');
    setTdsProjectReference(currentData.tds_project_reference || '');
  }, [open, isPublica]); // eslint-disable-line

  // Auto-calc discount % when Publica y hay estimated + offered
  const autoDiscount = (() => {
    if (!isPresentada || !isPublica) return '';
    const est = parseFloat(currentData.estimated_value ?? estimatedValue);
    const off = parseFloat(offeredAmount);
    if (!est || Number.isNaN(est) || Number.isNaN(off)) return '';
    return (Math.round(((est - off) / est) * 100 * 100) / 100).toString();
  })();

  // Auto-fill bidder as TDS when clientType != typsa y modalidad = solo
  const effectiveBidder = (isPresentada && !isTypsa && bidModality === 'solo') ? 'TDS' : bidder;

  const handleConfirm = () => {
    onConfirm({
      // Responsable: si mantiene, devuelve el actual; si cambia, el nuevo.
      owner: ownerMode === 'change' ? owner : (currentOwner || ''),
      reason,
      // Descartada — iter 92: motivo principal/secundario eliminados.
      // Only 'reason' (comment) and follow-up structures persist.
      discard_reason_primary: null,
      discard_reason_secondary: null,
      // Propuesta — nuevos campos que se persisten a formData del padre
      relevance: undefined, // Overridden below for Presentada/Propuesta
      relevance_comment: undefined,
      link_carpeta_trabajo: isPropuesta ? (linkCarpeta || null) : undefined,
      link_sharepoint: undefined, // Overridden below
      estimated_value: (isPropuesta && missingEstimated) ? estimatedValue : undefined,
      expected_delivery_date: (isPropuesta && (missingExpectedDelivery || !currentData.expected_delivery_date)) ? expectedDelivery : undefined,
      deadline_time: (isPropuesta && missingDeadlineTime) ? deadlineTime : undefined,
      link_pliego: (isPropuesta && missingLinkPliego) ? linkPliego : undefined,
      // Presentada — iter 90
      bid_modality: isPresentada && !isTypsa ? (bidModality || null) : (isPresentada && isTypsa ? 'subcontratista' : undefined),
      offered_amount: isPresentada ? (offeredAmount !== '' ? parseFloat(offeredAmount) : null) : undefined,
      tds_amount: (isPresentada && !isTypsa && (bidModality === 'ute' || bidModality === 'subcontratista'))
        ? (tdsAmount !== '' ? parseFloat(tdsAmount) : null)
        : undefined,
      delivery_date: isPresentada ? (deliveryDate || null) : undefined,
      bidder: (isPresentada && !isTypsa) ? (effectiveBidder || null) : undefined,
      discount_percentage: isPresentada
        ? (isPublica
            ? (autoDiscount !== '' ? parseFloat(autoDiscount) : null)
            : (!isTypsa ? (discountPct !== '' ? parseFloat(discountPct) : null) : undefined))
        : undefined,
      // Presentada may also update pending Propuesta fields (relevance + link_sharepoint + service_types)
      relevance: (isPropuesta || isPresentada) ? (relevance || null) : undefined,
      relevance_comment: (isPropuesta || isPresentada) ? (relevanceComment || null) : undefined,
      link_sharepoint: (isPropuesta || (isPresentada && !currentData.link_sharepoint)) ? (linkSharepoint || null) : undefined,
      // Feb 2026 — Presentada: si link_carpeta_trabajo aún está vacío en la
      // oportunidad, se copia lo que el usuario haya escrito en link_sharepoint.
      link_carpeta_trabajo: isPropuesta
        ? (linkCarpeta || null)
        : (isPresentada && !currentData.link_carpeta_trabajo
            ? (linkSharepoint || null)
            : undefined),
      // Tipos de servicios — rellenados por primera vez si estaban vacíos.
      service_types: ((isPropuesta || isPresentada) && (!currentData.service_types || currentData.service_types.length === 0))
        ? (serviceTypes.length > 0 ? serviceTypes : null)
        : undefined,
      other_service_type: ((isPropuesta || isPresentada) && (!currentData.service_types || currentData.service_types.length === 0))
        ? (serviceTypes.includes('Otros') ? (otherServiceType || null) : null)
        : undefined,
      // Ganada / Perdida — iter 91
      award_amount: undefined, // Managed above
      awardee_discount: undefined,
      number_of_bidders: undefined,
      final_awardee: undefined,
      global_position: undefined,
      technical_position: undefined,
      economic_position: undefined,
      result_comments: (isGanada || isPerdida) ? (resultComments || null) : undefined,
      // Salto a Producción — solo en Ganada, opcional
      tds_project_reference: isGanada ? (tdsProjectReference || null) : undefined,
      // Presentada / Descartada (existentes)
      expected_resolution_date: (isDescartada || isPresentada) ? (resolutionDate || null) : null,
      create_followup_task: (isDescartada || isPresentada) ? createFollowup : null,
      // Iter 92: solo Pública Descartada tiene "hacer seguimiento" con responsable asignado.
      follow_up_active: isDescartada && isPublica ? followUpActive : (isDescartada ? false : null),
      follow_up_owner: (isDescartada && isPublica && followUpActive) ? (followUpOwner || null) : (isDescartada ? null : undefined),
      // Los 7 campos de resultado se rellenan opcionalmente en Descartada Pública con seguimiento
      award_amount: (isDescartada && isPublica && followUpActive && awardAmount !== '')
        ? parseFloat(awardAmount)
        : ((isGanada || isPerdida) ? (awardAmount !== '' ? parseFloat(awardAmount) : null) : undefined),
      awardee_discount: (isDescartada && isPublica && followUpActive && awardeeDiscount !== '')
        ? parseFloat(awardeeDiscount)
        : ((isPerdida && isPublica) ? (awardeeDiscount !== '' ? parseFloat(awardeeDiscount) : null) : ((isPerdida && isPrivada && awardeeDiscount !== '') ? parseFloat(awardeeDiscount) : undefined)),
      number_of_bidders: (isDescartada && isPublica && followUpActive && numberOfBidders !== '')
        ? parseInt(numberOfBidders, 10)
        : (((isGanada || isPerdida) && numberOfBidders !== '') ? parseInt(numberOfBidders, 10) : (((isGanada && isPublica) || (isPerdida && isPublica)) && numberOfBidders === '' ? null : undefined)),
      final_awardee: (isDescartada && isPublica && followUpActive)
        ? (finalAwardee || null)
        : (isPerdida ? (finalAwardee || null) : undefined),
      global_position: (isDescartada && isPublica && followUpActive && globalPosition !== '')
        ? parseInt(globalPosition, 10)
        : ((isPerdida && globalPosition !== '') ? parseInt(globalPosition, 10) : undefined),
      technical_position: (isDescartada && isPublica && followUpActive && technicalPosition !== '')
        ? parseInt(technicalPosition, 10)
        : (((isGanada || isPerdida) && technicalPosition !== '') ? parseInt(technicalPosition, 10) : undefined),
      economic_position: (isDescartada && isPublica && followUpActive && economicPosition !== '')
        ? parseInt(economicPosition, 10)
        : (((isGanada || isPerdida) && economicPosition !== '') ? parseInt(economicPosition, 10) : undefined),
    });
  };

  const handleCancel = () => onCancel();

  // Propuesta — required fields (relajado si backward)
  const propuestaMissing = isPropuesta && !isBackward && (
    !reason.trim() ||
    !relevance ||
    !relevanceComment.trim() ||
    !linkCarpeta.trim() ||
    (missingEstimated && (estimatedValue === '' || estimatedValue === null || estimatedValue === undefined)) ||
    (missingExpectedDelivery && !expectedDelivery) ||
    (missingDeadlineTime && !deadlineTime) ||
    (missingLinkPliego && !linkPliego) ||
    (ownerMode === 'change' && !owner)
  );

  // Iter 93: en backward solo comentario + responsable son obligatorios
  const backwardMissing = isBackward && (
    !reason.trim() ||
    (ownerMode === 'change' && !owner)
  );

  // (confirmDisabled se declara al final, tras todos los *Missing*)

  // Iter 93: en transiciones hacia atrás relajamos todas las obligaciones
  // salvo el comentario del cambio + responsable. Los campos siguen visibles
  // rellenos con los datos previos para que el usuario reconfirme o corrija.
  const descartadaMissing = isDescartada && !isBackward && (
    !reason.trim() ||
    (isPublica && followUpActive && !followUpOwner) ||
    (ownerMode === 'change' && !owner)
  );

  const presentadaMissing = isPresentada && !isBackward && (
    !reason.trim() ||
    // Relevancia/comentario: solo obligatorios en la transición si NO estaban ya rellenados.
    (!currentData.relevance && !relevance) ||
    (!currentData.relevance_comment && !relevanceComment.trim()) ||
    (!isTypsa && !currentData.link_sharepoint && !linkSharepoint.trim()) ||
    // Tipos de servicios: obligatorio si aún no se rellenó.
    ((!currentData.service_types || currentData.service_types.length === 0) && serviceTypes.length === 0) ||
    (serviceTypes.includes('Otros') && !otherServiceType.trim()) ||
    (!isTypsa && !bidModality) ||
    (offeredAmount === '' || offeredAmount === null || offeredAmount === undefined) ||
    (!isTypsa && (bidModality === 'ute' || bidModality === 'subcontratista') && (tdsAmount === '' || tdsAmount === null || tdsAmount === undefined)) ||
    !deliveryDate ||
    (!isTypsa && bidModality !== 'solo' && !bidder.trim()) ||
    (ownerMode === 'change' && !owner)
  );

  // Ganada — award_amount es auto (= offered_amount) para TODOS los scopes; ya no se exige.
  // Solo se pide result_comments + reason obligatorio. Relajado si isBackward.
  const ganadaMissing = isGanada && !isBackward && (
    !reason.trim() ||
    !resultComments.trim() ||
    (isPublica && (
      numberOfBidders === '' || numberOfBidders === null || numberOfBidders === undefined ||
      technicalPosition === '' || technicalPosition === null || technicalPosition === undefined ||
      economicPosition === '' || economicPosition === null || economicPosition === undefined
    )) ||
    (ownerMode === 'change' && !owner)
  );

  // Perdida — obligatorios según scope (iter 91). Relajado si isBackward.
  const perdidaMissing = isPerdida && !isBackward && (
    !reason.trim() ||
    !resultComments.trim() ||
    (isPublica && (
      !finalAwardee.trim() ||
      awardAmount === '' || awardAmount === null || awardAmount === undefined ||
      awardeeDiscount === '' || awardeeDiscount === null || awardeeDiscount === undefined ||
      numberOfBidders === '' || numberOfBidders === null || numberOfBidders === undefined ||
      globalPosition === '' || globalPosition === null || globalPosition === undefined ||
      technicalPosition === '' || technicalPosition === null || technicalPosition === undefined ||
      economicPosition === '' || economicPosition === null || economicPosition === undefined
    )) ||
    (ownerMode === 'change' && !owner)
  );

  const confirmDisabled = descartadaMissing || propuestaMissing || presentadaMissing || ganadaMissing || perdidaMissing || backwardMissing;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col" data-testid="state-change-dialog">
        <DialogHeader className="shrink-0">
          <DialogTitle>Confirmar Cambio de Estado</DialogTitle>
          <DialogDescription>
            Estás a punto de cambiar el estado de esta oportunidad.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4 flex-1 overflow-y-auto">
          <div className="flex items-center justify-center gap-3 p-4 bg-muted/50 rounded-lg">
            <Badge className={stageColors[fromStage] || 'bg-gray-100'}>{fromStage}</Badge>
            <ArrowRight className="w-5 h-5 text-muted-foreground" />
            <Badge className={stageColors[toStage] || 'bg-gray-100'}>{toStage}</Badge>
          </div>

          {/* Iter 93: aviso de retroceso */}
          {isBackward && (
            <div className="rounded-md border border-amber-400 bg-amber-50 p-3 text-sm" data-testid="backward-banner">
              <p className="font-medium text-amber-800 flex items-center gap-1">
                <Info className="w-4 h-4" /> Estás volviendo a una fase anterior
              </p>
              <p className="text-xs text-amber-700 mt-1">
                Los datos de la fase <strong>{toStage}</strong> se muestran rellenos con los valores previos.
                Puedes reconfirmarlos o corregirlos: <strong>ninguno es obligatorio ahora</strong>. Lo único
                obligatorio es el comentario justificando el retroceso.
              </p>
            </div>
          )}

          {/* Responsable — obligatorio validar o cambiar */}
          <div className="space-y-2 rounded-md border p-3 bg-muted/20">
            <Label className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4" />
              Responsable de esta fase *
            </Label>
            <div className="flex flex-col gap-2">
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="ownerMode"
                  checked={ownerMode === 'keep'}
                  onChange={() => setOwnerMode('keep')}
                  data-testid="owner-mode-keep"
                />
                <span>Mantener responsable actual: <strong>{currentOwner || 'Sin asignar'}</strong></span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm">
                <input
                  type="radio"
                  name="ownerMode"
                  checked={ownerMode === 'change'}
                  onChange={() => setOwnerMode('change')}
                  data-testid="owner-mode-change"
                />
                <span>Cambiar responsable</span>
              </label>
              {ownerMode === 'change' && (
                <Select value={owner || 'none'} onValueChange={(v) => setOwner(v === 'none' ? '' : v)}>
                  <SelectTrigger data-testid="state-change-owner-select">
                    <SelectValue placeholder="Seleccionar nuevo responsable" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Seleccionar...</SelectItem>
                    {editorUsers?.map((u) => (
                      <SelectItem key={u.email} value={u.name}>{u.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
          </div>

          {/* Comentario del cambio — obligatorio en Propuesta, Presentada y Descartada */}
          <div className="space-y-2">
            <Label htmlFor="reason" className="flex items-center gap-1">
              {isDescartada ? 'Detalle del motivo *' :
                (isBackward ? 'Comentario justificando el retroceso *' :
                (isPropuesta || isPresentada ? 'Comentario justificando el cambio *' :
                (isGanada || isPerdida ? 'Comentario justificando el cambio *' : 'Comentario del cambio (opcional)')))}
              {isPresentada && (
                <span
                  className="inline-flex items-center text-muted-foreground cursor-help"
                  title="Explica cómo ha sido el proceso de preparación: quiénes participaron, dificultades, decisiones clave."
                  data-testid="reason-info-presentada"
                >
                  <Info className="w-3.5 h-3.5" />
                </span>
              )}
              {(isGanada || isPerdida) && (
                <span
                  className="inline-flex items-center text-muted-foreground cursor-help"
                  title={isGanada
                    ? 'Explica las claves del resultado favorable: puntos fuertes, competencia, hitos.'
                    : 'Explica los motivos por los que no hemos ganado: precio, propuesta técnica, competencia, etc.'}
                  data-testid={`reason-info-${isGanada ? 'ganada' : 'perdida'}`}
                >
                  <Info className="w-3.5 h-3.5" />
                </span>
              )}
            </Label>
            <Textarea
              id="reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={isDescartada
                ? 'Explique con más detalle el motivo del descarte'
                : (isPropuesta ? '¿Por qué pasamos a Propuesta?'
                : (isPresentada ? 'Cómo ha sido el proceso de preparación de la oferta'
                : 'Añadir un comentario sobre este cambio de estado'))}
              rows={3}
              data-testid="state-change-reason-textarea"
            />
          </div>

          {/* ─── Bloque específico de Propuesta ─────────────────────────────── */}
          {isPropuesta && (
            <>
              {/* Campos que faltaron en Detectada — se piden aquí */}
              {(missingEstimated || missingExpectedDelivery || missingDeadlineTime || missingLinkPliego) && (
                <div className="rounded-md border border-amber-300 bg-amber-50/60 p-3 space-y-3">
                  <p className="text-xs font-medium text-amber-800 uppercase tracking-wide flex items-center gap-1">
                    <Info className="w-3.5 h-3.5" />
                    Faltan datos de la fase Detectada
                  </p>
                  {missingEstimated && (
                    <div>
                      <Label htmlFor="estimated_value_dlg">
                        {isPublica ? 'Precio Base de Licitación (€) *' : 'Valor Estimado (€) *'}
                      </Label>
                      <Input
                        id="estimated_value_dlg"
                        type="number" step="0.01" min="0"
                        value={estimatedValue}
                        onChange={(e) => setEstimatedValue(e.target.value)}
                        placeholder="0.00"
                        data-testid="state-change-estimated-value"
                      />
                    </div>
                  )}
                  {missingExpectedDelivery && (
                    <div>
                      <Label htmlFor="expected_delivery_dlg">Fecha Límite de Entrega *</Label>
                      <Input
                        id="expected_delivery_dlg"
                        type="date"
                        value={expectedDelivery}
                        onChange={(e) => setExpectedDelivery(e.target.value)}
                        data-testid="state-change-expected-delivery"
                      />
                    </div>
                  )}
                  {missingDeadlineTime && (
                    <div>
                      <Label htmlFor="deadline_time_dlg">Hora Límite de Entrega *</Label>
                      <Input
                        id="deadline_time_dlg"
                        type="time"
                        value={deadlineTime}
                        onChange={(e) => setDeadlineTime(e.target.value)}
                        data-testid="state-change-deadline-time"
                      />
                    </div>
                  )}
                  {missingLinkPliego && (
                    <div>
                      <Label htmlFor="link_pliego_dlg" className="flex items-center gap-1">
                        Link a Pliego *
                        <span className="inline-flex items-center text-muted-foreground cursor-help"
                              title="Se recomienda copiar aquí el enlace del expediente en TYPSA Deal.">
                          <Info className="w-3.5 h-3.5" />
                        </span>
                      </Label>
                      <Input
                        id="link_pliego_dlg"
                        type="url"
                        value={linkPliego}
                        onChange={(e) => setLinkPliego(e.target.value)}
                        placeholder="https://..."
                        data-testid="state-change-link-pliego"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Relevancia + comentario — solo se piden si aún no están rellenos */}
              <div className="grid grid-cols-1 gap-3">
                {!currentData.relevance && (
                <div>
                  <Label htmlFor="relevance_dlg">Relevancia *</Label>
                  <Select value={relevance || 'none'} onValueChange={(v) => setRelevance(v === 'none' ? '' : v)}>
                    <SelectTrigger id="relevance_dlg" data-testid="state-change-relevance">
                      <SelectValue placeholder="Seleccionar relevancia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seleccionar...</SelectItem>
                      {RELEVANCE_OPTIONS.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                )}
                {!currentData.relevance_comment && (
                <div>
                  <Label htmlFor="relevance_comment_dlg">Comentario de relevancia *</Label>
                  <Textarea
                    id="relevance_comment_dlg"
                    value={relevanceComment}
                    onChange={(e) => setRelevanceComment(e.target.value)}
                    placeholder="¿Por qué esta relevancia?"
                    rows={2}
                    data-testid="state-change-relevance-comment"
                  />
                </div>
                )}
                {!currentData.link_carpeta_trabajo && (
                <div>
                  <Label htmlFor="link_carpeta_dlg">Link Carpeta de trabajo inicial *</Label>
                  <Input
                    id="link_carpeta_dlg"
                    type="url"
                    value={linkCarpeta}
                    onChange={(e) => setLinkCarpeta(e.target.value)}
                    placeholder="https://..."
                    data-testid="state-change-link-carpeta"
                  />
                </div>
                )}
                {/* Link SharePoint — NUNCA aparece en TYPSA (no aplica). */}
                {!isTypsa && (
                  <div>
                    <Label htmlFor="link_sharepoint_dlg">Link SharePoint de la oferta (opcional)</Label>
                    <Input
                      id="link_sharepoint_dlg"
                      type="url"
                      value={linkSharepoint}
                      onChange={(e) => setLinkSharepoint(e.target.value)}
                      placeholder="https://... (puedes rellenarlo más tarde)"
                      data-testid="state-change-link-sharepoint"
                    />
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                      <Info className="w-3.5 h-3.5" />
                      Si lo dejas vacío, la oportunidad quedará marcada como <strong>pendiente de SharePoint</strong> hasta que lo rellenes.
                    </p>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ─── Bloque específico de Descartada (iter 92 rediseñado) ───────── */}
          {isDescartada && (
            <div className="space-y-3 rounded-md border p-3 bg-slate-50/40">
              <p className="text-xs text-muted-foreground italic">
                El comentario del cambio (arriba) es el motivo del descarte. No se pide un motivo aparte.
              </p>

              {/* Fecha prevista de resolución (heredado) — se sigue mostrando abajo por el bloque común */}

              {/* Solo Pública: botón/toggle "Hacer seguimiento" */}
              {isPublica && (
                <div className="rounded-md border p-3 bg-white">
                  <div className="flex items-start gap-2">
                    <input
                      id="follow_up_active_dlg"
                      type="checkbox"
                      className="mt-1 w-4 h-4"
                      checked={followUpActive}
                      onChange={(e) => setFollowUpActive(e.target.checked)}
                      data-testid="descartada-follow-up-toggle"
                    />
                    <div className="flex-1">
                      <Label htmlFor="follow_up_active_dlg" className="cursor-pointer text-sm font-medium">
                        Hacer seguimiento del resultado (Pública)
                      </Label>
                      <p className="text-xs text-muted-foreground mt-0.5">
                        Activa esto para registrar la adjudicación aunque hayamos descartado presentarnos. Los campos son opcionales pero quedará pendiente hasta rellenarlos.
                      </p>
                    </div>
                  </div>

                  {followUpActive && (
                    <div className="mt-3 space-y-3 border-t pt-3">
                      {/* Responsable del seguimiento — obligatorio */}
                      <div>
                        <Label htmlFor="follow_up_owner_dlg">Responsable del seguimiento *</Label>
                        <Select value={followUpOwner || 'none'} onValueChange={(v) => setFollowUpOwner(v === 'none' ? '' : v)}>
                          <SelectTrigger id="follow_up_owner_dlg" data-testid="descartada-follow-up-owner">
                            <SelectValue placeholder="Seleccionar responsable de seguimiento" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Seleccionar...</SelectItem>
                            {editorUsers?.map((u) => (
                              <SelectItem key={u.email} value={u.name}>{u.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">
                          A esta persona le aparecerá en su Panel un pendiente de completar el resultado hasta que rellene los 7 campos.
                        </p>
                      </div>

                      {/* Los 7 campos de resultado (opcionales) */}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label htmlFor="final_awardee_desc">Adjudicatario Final</Label>
                          <Input id="final_awardee_desc" value={finalAwardee} onChange={(e) => setFinalAwardee(e.target.value)} data-testid="state-change-final-awardee" />
                        </div>
                        <div>
                          <Label htmlFor="award_amount_desc">Importe Adjudicación (€)</Label>
                          <Input id="award_amount_desc" type="number" step="0.01" min="0" value={awardAmount} onChange={(e) => setAwardAmount(e.target.value)} data-testid="state-change-award-amount" />
                        </div>
                        <div>
                          <Label htmlFor="awardee_discount_desc">Baja Adjudicatario (%)</Label>
                          <Input id="awardee_discount_desc" type="number" step="0.01" min="0" max="100" value={awardeeDiscount} onChange={(e) => setAwardeeDiscount(e.target.value)} data-testid="state-change-awardee-discount" />
                        </div>
                        <div>
                          <Label htmlFor="number_of_bidders_desc">Nº Licitadores</Label>
                          <Input id="number_of_bidders_desc" type="number" min="1" value={numberOfBidders} onChange={(e) => setNumberOfBidders(e.target.value)} data-testid="state-change-number-of-bidders" />
                        </div>
                        <div>
                          <Label htmlFor="global_position_desc">Posición Global</Label>
                          <Input id="global_position_desc" type="number" min="1" value={globalPosition} onChange={(e) => setGlobalPosition(e.target.value)} data-testid="state-change-global-position" />
                        </div>
                        <div>
                          <Label htmlFor="technical_position_desc">Posición Técnica</Label>
                          <Input id="technical_position_desc" type="number" min="1" value={technicalPosition} onChange={(e) => setTechnicalPosition(e.target.value)} data-testid="state-change-technical-position" />
                        </div>
                        <div>
                          <Label htmlFor="economic_position_desc">Posición Económica</Label>
                          <Input id="economic_position_desc" type="number" min="1" value={economicPosition} onChange={(e) => setEconomicPosition(e.target.value)} data-testid="state-change-economic-position" />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {(false) && isDescartada && (
            <>
              <div className="space-y-2">
                <Label htmlFor="discard_primary" className="text-amber-700">Motivo principal *</Label>
                <Select value={primary} onValueChange={setPrimary}>
                  <SelectTrigger id="discard_primary" data-testid="discard-primary-select">
                    <SelectValue placeholder="Selecciona un motivo" />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCARD_REASON_OPTIONS.map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor="discard_secondary">Motivo secundario (opcional)</Label>
                <Select value={secondary} onValueChange={setSecondary}>
                  <SelectTrigger id="discard_secondary" data-testid="discard-secondary-select">
                    <SelectValue placeholder="Sin motivo secundario" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Sin motivo secundario</SelectItem>
                    {DISCARD_REASON_OPTIONS.map((o) => (
                      <SelectItem key={o.key} value={o.key}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* ─── Bloque específico de Presentada (iter 90) ──────────────────── */}
          {isPresentada && (
            <div className="space-y-3">
              {/* Tipos de servicios — obligatorio si aún no se han seleccionado */}
              {(!currentData.service_types || currentData.service_types.length === 0) && (
                <div className="rounded-md border p-3 bg-slate-50/60">
                  <Label className="text-sm">Tipos de servicios *</Label>
                  <p className="text-xs text-muted-foreground mb-2">Selecciona los que aplican a esta oferta.</p>
                  <div className="grid grid-cols-2 gap-2">
                    {['BIM','GIS','Gobernanza del Dato','Inteligencia Artificial','Software a Medida','Gemelos Digitales','Visualización Avanzada','Otros'].map((s) => {
                      const checked = serviceTypes.includes(s);
                      return (
                        <label key={s} className="flex items-center gap-2 text-sm cursor-pointer">
                          <input
                            type="checkbox"
                            className="w-4 h-4"
                            checked={checked}
                            onChange={(e) => {
                              if (e.target.checked) setServiceTypes([...serviceTypes, s]);
                              else {
                                setServiceTypes(serviceTypes.filter(x => x !== s));
                                if (s === 'Otros') setOtherServiceType('');
                              }
                            }}
                            data-testid={`state-change-service-${s}`}
                          />
                          <span>{s}</span>
                        </label>
                      );
                    })}
                  </div>
                  {serviceTypes.includes('Otros') && (
                    <div className="mt-2">
                      <Label htmlFor="other_service_type_dlg" className="text-xs">Especifica el otro tipo *</Label>
                      <Input
                        id="other_service_type_dlg"
                        value={otherServiceType}
                        onChange={(e) => setOtherServiceType(e.target.value)}
                        placeholder="Descripción del tipo de servicio"
                        data-testid="state-change-other-service"
                      />
                    </div>
                  )}
                </div>
              )}

              {/* Modalidad de participación (Pública/Privada; no aplica a TYPSA) — solo si falta */}
              {!isTypsa && !currentData.bid_modality && (
                <div>
                  <Label htmlFor="bid_modality">Modalidad de participación *</Label>
                  <Select value={bidModality || 'none'} onValueChange={(v) => setBidModality(v === 'none' ? '' : v)}>
                    <SelectTrigger id="bid_modality" data-testid="state-change-bid-modality">
                      <SelectValue placeholder="Seleccionar modalidad" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seleccionar...</SelectItem>
                      <SelectItem value="solo">En solitario</SelectItem>
                      <SelectItem value="ute">En UTE</SelectItem>
                      <SelectItem value="subcontratista">Como subcontratista</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {(currentData.offered_amount === null || currentData.offered_amount === undefined || currentData.offered_amount === '') && (
                <div>
                  <Label htmlFor="offered_amount_dlg">Importe Total Ofertado (€) *</Label>
                  <Input
                    id="offered_amount_dlg"
                    type="number" step="0.01" min="0"
                    value={offeredAmount}
                    onChange={(e) => setOfferedAmount(e.target.value)}
                    placeholder="0.00"
                    data-testid="state-change-offered-amount"
                  />
                </div>
                )}

                {/* Importe para TDS: solo Pub/Priv + UTE o subcontratista, y solo si falta */}
                {!isTypsa && (bidModality === 'ute' || bidModality === 'subcontratista') && (currentData.tds_amount === null || currentData.tds_amount === undefined || currentData.tds_amount === '') && (
                  <div>
                    <Label htmlFor="tds_amount_dlg">Importe para TDS (€) *</Label>
                    <Input
                      id="tds_amount_dlg"
                      type="number" step="0.01" min="0"
                      value={tdsAmount}
                      onChange={(e) => setTdsAmount(e.target.value)}
                      placeholder="0.00"
                      data-testid="state-change-tds-amount"
                    />
                  </div>
                )}

                {!currentData.delivery_date && (
                <div>
                  <Label htmlFor="delivery_date_dlg">Fecha Real de Entrega *</Label>
                  <Input
                    id="delivery_date_dlg"
                    type="date"
                    value={deliveryDate}
                    onChange={(e) => setDeliveryDate(e.target.value)}
                    data-testid="state-change-delivery-date"
                  />
                </div>
                )}

                {/* Nombre entidad licitadora: solo Pub/Priv y solo si falta. En solitario auto TDS */}
                {!isTypsa && !currentData.bidder && bidModality !== 'solo' && (
                  <div>
                    <Label htmlFor="bidder_dlg">Entidad Licitadora *</Label>
                    <Input
                      id="bidder_dlg"
                      value={bidder}
                      onChange={(e) => setBidder(e.target.value)}
                      placeholder="Nombre del licitador principal"
                      data-testid="state-change-bidder"
                    />
                  </div>
                )}

                {/* Porcentaje de baja */}
                {isPublica && (
                  <div>
                    <Label htmlFor="discount_dlg">Porcentaje de Baja (%) <span className="text-xs text-muted-foreground">(auto)</span></Label>
                    <Input
                      id="discount_dlg"
                      type="text"
                      readOnly
                      value={autoDiscount ? `${autoDiscount}%` : ''}
                      className="bg-muted/40 text-muted-foreground"
                      data-testid="state-change-discount-auto"
                    />
                    <p className="text-xs text-muted-foreground mt-1">Se calcula al rellenar Importe Ofertado + Precio Base.</p>
                  </div>
                )}
                {clientType === 'privada' && !(currentData.discount_percentage != null && currentData.discount_percentage !== '') && (
                  <div>
                    <Label htmlFor="discount_priv_dlg" className="flex items-center gap-1">
                      Porcentaje de Baja (%) (opcional)
                      <span
                        className="inline-flex items-center text-muted-foreground cursor-help"
                        title="Solo si el cliente ha compartido un precio de referencia."
                        data-testid="state-change-discount-info"
                      >
                        <Info className="w-3.5 h-3.5" />
                      </span>
                    </Label>
                    <Input
                      id="discount_priv_dlg"
                      type="number" step="0.01" min="0" max="100"
                      value={discountPct}
                      onChange={(e) => setDiscountPct(e.target.value)}
                      placeholder="0.00"
                      data-testid="state-change-discount-optional"
                    />
                  </div>
                )}
              </div>

              {/* Link SharePoint — si estaba pendiente, aquí ya es obligatorio */}
              {!currentData.link_sharepoint && !isTypsa && (
                <div className="rounded-md border border-amber-300 bg-amber-50/60 p-3">
                  <Label htmlFor="link_sharepoint_pres" className="flex items-center gap-1">
                    Link SharePoint de la oferta *
                    <span className="inline-flex items-center text-amber-700 cursor-help"
                          title="Estaba marcado como pendiente en Propuesta; al presentar la oferta es obligatorio.">
                      <Info className="w-3.5 h-3.5" />
                    </span>
                  </Label>
                  <Input
                    id="link_sharepoint_pres"
                    type="url"
                    value={linkSharepoint}
                    onChange={(e) => setLinkSharepoint(e.target.value)}
                    placeholder="https://..."
                    data-testid="state-change-link-sharepoint"
                  />
                </div>
              )}

              {/* Relevancia — solo se pide si NO estaba ya rellenada en Propuesta.
                  Si ya está, se mantiene silenciosa (editable desde el formulario). */}
              {(!currentData.relevance || !currentData.relevance_comment) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="relevance_pres">Relevancia *</Label>
                  <Select value={relevance || 'none'} onValueChange={(v) => setRelevance(v === 'none' ? '' : v)}>
                    <SelectTrigger id="relevance_pres" data-testid="state-change-relevance">
                      <SelectValue placeholder="Seleccionar relevancia" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Seleccionar...</SelectItem>
                      {RELEVANCE_OPTIONS.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="relevance_comment_pres">Comentario de Relevancia *</Label>
                  <Textarea
                    id="relevance_comment_pres"
                    value={relevanceComment}
                    onChange={(e) => setRelevanceComment(e.target.value)}
                    rows={2}
                    data-testid="state-change-relevance-comment"
                  />
                </div>
              </div>
              )}
            </div>
          )}

          {/* ─── Bloque Ganada / Perdida (iter 91) ─────────────────────────── */}
          {(isGanada || isPerdida) && (
            <div className="space-y-3 rounded-md border p-3 bg-slate-50/40">
              <p className="text-xs font-medium text-slate-700 uppercase tracking-wide">
                Resultado Final {isGanada ? '(Ganada)' : '(Perdida)'}
              </p>

              {isTypsa && (
                <p className="text-xs text-muted-foreground italic">
                  Como es una oportunidad TYPSA, solo se requiere el comentario del resultado.
                </p>
              )}

              {/* Comentarios del resultado — obligatorio en TODOS los scopes */}
              <div>
                <Label htmlFor="result_comments_dlg">Comentarios del resultado *</Label>
                <Textarea
                  id="result_comments_dlg"
                  value={resultComments}
                  onChange={(e) => setResultComments(e.target.value)}
                  rows={2}
                  placeholder={isGanada ? '¿Qué claves nos han hecho ganar?' : '¿Por qué no hemos ganado?'}
                  data-testid="state-change-result-comments"
                />
              </div>

              {/* Campos adicionales — solo para Privada y Pública (nunca TYPSA) */}
              {!isTypsa && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {/* Importe Adjudicación — Ganada: auto = ofertado en TODOS los scopes,
                      no se pide en la transición. Solo Perdida lo pide manualmente. */}
                  {/* Perdida y Privada → todos son opcionales; Perdida y Pública → obligatorios.
                      Cada campo solo aparece si aún no está relleno. */}
                  {isPerdida && (
                    <>
                      {!currentData.final_awardee && (
                      <div>
                        <Label htmlFor="final_awardee_dlg">Adjudicatario Final{isPublica ? ' *' : ''}</Label>
                        <Input
                          id="final_awardee_dlg"
                          value={finalAwardee}
                          onChange={(e) => setFinalAwardee(e.target.value)}
                          placeholder="Empresa que ha ganado"
                          data-testid="state-change-final-awardee"
                        />
                      </div>
                      )}
                      {(currentData.award_amount === null || currentData.award_amount === undefined || currentData.award_amount === '') && (
                      <div>
                        <Label htmlFor="award_amount_dlg">Importe Adjudicación (€){isPublica ? ' *' : ''}</Label>
                        <Input
                          id="award_amount_dlg"
                          type="number" step="0.01" min="0"
                          value={awardAmount}
                          onChange={(e) => setAwardAmount(e.target.value)}
                          data-testid="state-change-award-amount"
                        />
                      </div>
                      )}
                      {(currentData.awardee_discount === null || currentData.awardee_discount === undefined || currentData.awardee_discount === '') && (
                      <div>
                        <Label htmlFor="awardee_discount_dlg">Baja Empresa Adjudicataria (%){isPublica ? ' *' : ''}</Label>
                        <Input
                          id="awardee_discount_dlg"
                          type="number" step="0.01" min="0" max="100"
                          value={awardeeDiscount}
                          onChange={(e) => setAwardeeDiscount(e.target.value)}
                          data-testid="state-change-awardee-discount"
                        />
                      </div>
                      )}
                    </>
                  )}

                  {/* Nº licitadores + posiciones — solo cuando aplican Y aún no están rellenos */}
                  {(isGanada || isPerdida) && (
                    <>
                      {(currentData.number_of_bidders === null || currentData.number_of_bidders === undefined || currentData.number_of_bidders === '') && (
                      <div>
                        <Label htmlFor="number_of_bidders_dlg">Nº de Licitadores{isPublica ? ' *' : ''}</Label>
                        <Input
                          id="number_of_bidders_dlg"
                          type="number" min="1"
                          value={numberOfBidders}
                          onChange={(e) => setNumberOfBidders(e.target.value)}
                          data-testid="state-change-number-of-bidders"
                        />
                      </div>
                      )}
                      {/* Posiciones: no se piden en Privada + Ganada. */}
                      {!(isPrivada && isGanada) && (
                        <>
                          {isPerdida && (currentData.global_position === null || currentData.global_position === undefined || currentData.global_position === '') && (
                            <div>
                              <Label htmlFor="global_position_dlg">Posición Global{isPublica ? ' *' : ''}</Label>
                              <Input
                                id="global_position_dlg"
                                type="number" min="1"
                                value={globalPosition}
                                onChange={(e) => setGlobalPosition(e.target.value)}
                                data-testid="state-change-global-position"
                              />
                            </div>
                          )}
                          {(currentData.technical_position === null || currentData.technical_position === undefined || currentData.technical_position === '') && (
                          <div>
                            <Label htmlFor="technical_position_dlg">Posición Técnica{isPublica ? ' *' : ''}</Label>
                            <Input
                              id="technical_position_dlg"
                              type="number" min="1"
                              value={technicalPosition}
                              onChange={(e) => setTechnicalPosition(e.target.value)}
                              data-testid="state-change-technical-position"
                            />
                          </div>
                          )}
                          {(currentData.economic_position === null || currentData.economic_position === undefined || currentData.economic_position === '') && (
                          <div>
                            <Label htmlFor="economic_position_dlg">Posición Económica{isPublica ? ' *' : ''}</Label>
                            <Input
                              id="economic_position_dlg"
                              type="number" min="1"
                              value={economicPosition}
                              onChange={(e) => setEconomicPosition(e.target.value)}
                              data-testid="state-change-economic-position"
                            />
                          </div>
                          )}
                        </>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* Salto a Producción — SOLO en Ganada, todos los scopes, opcional (pendiente) */}
              {isGanada && (
                <div className="rounded-md border border-amber-300 bg-amber-50/60 p-3">
                  <Label htmlFor="tds_project_reference_dlg" className="flex items-center gap-1">
                    Proyecto de referencia TDS
                    <span
                      className="inline-flex items-center text-amber-700 cursor-help"
                      title="Es opcional: puedes dejarlo vacío ahora y rellenarlo más tarde. La oportunidad quedará marcada como pendiente hasta que lo rellenes."
                      data-testid="tds-project-info"
                    >
                      <Info className="w-3.5 h-3.5" />
                    </span>
                  </Label>
                  <Input
                    id="tds_project_reference_dlg"
                    value={tdsProjectReference}
                    onChange={(e) => setTdsProjectReference(e.target.value)}
                    placeholder="Ej: TDS-2026-042"
                    data-testid="state-change-tds-project"
                  />
                  <p className="text-xs text-amber-700 mt-1">
                    Si lo dejas vacío la oportunidad quedará marcada como <strong>pendiente de Proyecto TDS</strong>.
                  </p>
                </div>
              )}
            </div>
          )}

          {(isDescartada || isPresentada) && !isDescartada && (
            <>
              <div className="space-y-2">
                <Label htmlFor="expected_resolution_date">Fecha prevista de resolución / adjudicación</Label>
                <Input
                  id="expected_resolution_date"
                  type="date"
                  value={resolutionDate}
                  onChange={(e) => setResolutionDate(e.target.value)}
                  data-testid="expected-resolution-date-input"
                />
                <p className="text-xs text-muted-foreground">
                  Sugerido: entrega + {isPublica ? '90' : '30'} días. Editable.
                </p>
              </div>
              <div className="rounded-md border p-3 bg-blue-50/40">
                <div className="flex items-start gap-2">
                  <input
                    id="create_followup" type="checkbox" className="mt-1"
                    data-testid="create-followup-checkbox"
                    checked={createFollowup}
                    onChange={(e) => setCreateFollowup(e.target.checked)}
                  />
                  <div className="flex-1">
                    <Label htmlFor="create_followup" className="cursor-pointer text-sm">
                      Crear tarea de seguimiento de adjudicación
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {isPublica
                        ? 'Pública: recomendado. Se creará una tarea con la fecha prevista.'
                        : 'Privada/TYPSA: por defecto desactivado. Actívalo si quieres una tarea.'}
                    </p>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 pt-4 border-t">
          <Button type="button" variant="outline" onClick={handleCancel} data-testid="cancel-state-change-btn">
            Cancelar
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={confirmDisabled}
            data-testid="confirm-state-change-btn"
          >
            Confirmar Cambio
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default StateChangeDialog;
